import { tracked } from "@glimmer/tracking";
import EmberObject, { computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import deprecated from "discourse/lib/deprecated";
import { getOwnerWithFallback } from "discourse/lib/get-owner";
import getURL from "discourse/lib/get-url";
import { deepMerge } from "discourse/lib/object";
import { emojiUnescape } from "discourse/lib/text";
import {
  hasTrackedFilter,
  isTrackedTopic,
} from "discourse/lib/topic-list-tracked-filter";
import Category from "discourse/models/category";
import Site from "discourse/models/site";
import User from "discourse/models/user";
import { i18n } from "discourse-i18n";

export default class NavItem extends EmberObject {
  static extraArgsCallbacks = [];
  static customNavItemHrefs = [];
  static extraNavItemDescriptors = [];

  static pathFor(filterType, context) {
    let path = getURL("");
    let includesCategoryContext = false;
    let includesTagContext = false;

    if (filterType === "categories") {
      path += "/categories";
      return path;
    }

    const tag = context.tag;
    if (tag?.slug && Site.currentProp("filters").includes(filterType)) {
      includesTagContext = true;

      if (context.category) {
        path += "/tags";
      } else {
        path += "/tag";
      }
    }

    if (context.category) {
      includesCategoryContext = true;
      path += `/c/${Category.slugFor(context.category)}/${context.category.id}`;

      if (context.noSubcategories) {
        path += "/none";
      }
    }

    if (includesTagContext) {
      // use canonical URL format with slug/id when id is available
      path += tag.id ? `/${tag.slug}/${tag.id}` : `/${tag.slug}`;
    }

    if (includesTagContext || includesCategoryContext) {
      path += "/l";
    }

    path += `/${filterType}`;

    // In the case of top, the nav item doesn't include a period because the
    // period has its own selector just below

    return path;
  }

  // Create a nav item given a filterType. It returns null if there is not
  // valid nav item. The name is a historical artifact.
  static fromText(filterType, opts) {
    const anonymous = !User.current();

    opts = opts || {};

    if (anonymous) {
      const topMenuItems = Site.currentProp("anonymous_top_menu_items");
      if (!topMenuItems || !topMenuItems.includes(filterType)) {
        return null;
      }
    }

    if (!Category.list() && filterType === "categories") {
      return null;
    }
    if (!Site.currentProp("top_menu_items").includes(filterType)) {
      return null;
    }

    let args = { name: filterType, hasIcon: filterType === "unread" };
    if (opts.category) {
      args.category = opts.category;
    }
    if (opts.tag) {
      args.tag = opts.tag;
    }
    if (opts.currentRouteQueryParams) {
      args.currentRouteQueryParams = opts.currentRouteQueryParams;
    }
    if (opts.noSubcategories) {
      args.noSubcategories = true;
    }
    NavItem.extraArgsCallbacks.forEach((cb) =>
      deepMerge(args, cb.call(this, filterType, opts))
    );

    let store = getOwnerWithFallback(this).lookup("service:store");
    return store.createRecord("nav-item", args);
  }

  static buildList(category, args) {
    args = args || {};

    if (category) {
      args.category = category;
    }

    if (!args.siteSettings) {
      deprecated("You must supply `buildList` with a `siteSettings` object", {
        since: "2.6.0",
        id: "discourse.nav-item.built-list-site-settings",
      });
      args.siteSettings = getOwnerWithFallback(this).lookup(
        "service:site-settings"
      );
    }
    let items = args.siteSettings.top_menu.split("|");

    const user = getOwnerWithFallback(this).lookup("service:current-user");
    if (user?.unified_new_enabled) {
      items = items.filter((item) => item !== "unread");
    }
    const filterType = (args.filterMode || "").split("/").pop();

    if (!items.some((i) => filterType === i)) {
      items.push(filterType);
    }

    items = items
      .map((i) => NavItem.fromText(i, args))
      .filter((i) => {
        if (i === null) {
          return false;
        }

        if (
          (category || args.skipCategoriesNavItem) &&
          i.name.startsWith("categor")
        ) {
          return false;
        }

        return true;
      });

    const context = {
      category: args.category,
      tag: args.tag,
      tagId: args.tagId,
      noSubcategories: args.noSubcategories,
    };

    const extraItems = NavItem.extraNavItemDescriptors
      .map((descriptor) =>
        ExtraNavItem.create(deepMerge({}, context, descriptor))
      )
      .filter((item) => {
        if (!item.customFilter) {
          return true;
        }
        return item.customFilter(category, args);
      });

    let forceActive = false;

    extraItems.forEach((item) => {
      if (item.init) {
        item.init(item, category, args);
      }

      const before = item.before;
      if (before) {
        let i;
        for (i = 0; i < items.length; i++) {
          if (items[i].name === before) {
            break;
          }
        }
        items.splice(i, 0, item);
      } else {
        items.push(item);
      }

      if (item.customHref) {
        item.href = item.customHref(category, args);
      } else if (item.href) {
        item.href = getURL(item.href);
      }

      if (item.forceActive && item.forceActive(category, args)) {
        item.active = true;
        forceActive = true;
      } else {
        item.active = undefined;
      }
    });

    if (forceActive) {
      items.forEach((i) => {
        if (i.active === undefined) {
          i.active = false;
        }
      });
    }
    return items;
  }

  @service topicTrackingState;
  @service currentUser;

  @tracked name;
  @tracked tag;

  @tracked _title;
  @tracked _displayName;

  @tracked _filterTypeOverride;

  @dependentKeyCompat
  get filterType() {
    if (this._filterTypeOverride !== undefined) {
      return this._filterTypeOverride;
    }
    return this.name;
  }

  set filterType(value) {
    this._filterTypeOverride = value;
  }

  @dependentKeyCompat
  get title() {
    if (this._title) {
      return this._title;
    }

    let nameKey = this.name.replace("/", ".");

    if (nameKey === "new" && this.currentUser?.unified_new_enabled) {
      nameKey = "unified_new";
    }

    return i18n("filters." + nameKey + ".help", {});
  }

  set title(value) {
    this._title = value;
  }

  @dependentKeyCompat
  get displayName() {
    if (this._displayName) {
      return this._displayName;
    }

    let count = this.count || 0;

    if (
      this.name === "latest" &&
      (Site.currentProp("desktopView") || this.tag !== undefined)
    ) {
      count = 0;
    }

    let extra = { count };
    const titleKey = count === 0 ? ".title" : ".title_with_count";

    return emojiUnescape(
      i18n(`filters.${this.name.replace("/", ".") + titleKey}`, extra)
    );
  }

  set displayName(value) {
    this._displayName = value;
  }

  @computed("filterType", "category", "noSubcategories", "tag")
  get href() {
    let customHref = null;

    NavItem.customNavItemHrefs.forEach(function (cb) {
      customHref = cb.call(this, this);
      if (customHref) {
        return false;
      }
    }, this);

    if (customHref) {
      return getURL(customHref);
    }

    const context = {
      category: this.category,
      noSubcategories: this.noSubcategories,
      tag: this.tag,
    };
    return NavItem.pathFor(this.filterType, context);
  }

  @computed("name", "category", "noSubcategories")
  get filterMode() {
    let mode = "";
    if (this.category) {
      mode += "c/";
      mode += Category.slugFor(this.category);
      if (this.noSubcategories) {
        mode += "/none";
      }
      mode += "/l/";
    }
    return mode + this.name.replace(" ", "-");
  }

  @computed(
    "name",
    "category",
    "tag",
    "noSubcategories",
    "currentRouteQueryParams",
    "topicTrackingState.messageCount"
  )
  get count() {
    return this.topicTrackingState?.lookupCount({
      type: this.name,
      category: this.category,
      tagId: this.tag?.id,
      noSubcategories: this.noSubcategories,
      customFilterFn: hasTrackedFilter(this.currentRouteQueryParams)
        ? isTrackedTopic
        : undefined,
    });
  }
}

export class ExtraNavItem extends NavItem {
  @tracked href;
  @tracked count = 0;

  /** @type {((category: Category | null | undefined, args: Record<string, unknown>) => boolean) | null} */
  customFilter = null;
}

export function extraNavItemProperties(cb) {
  NavItem.extraArgsCallbacks.push(cb);
}

export function customNavItemHref(cb) {
  NavItem.customNavItemHrefs.push(cb);
}

export function clearNavItems() {
  NavItem.customNavItemHrefs.length = 0;
  NavItem.extraArgsCallbacks.length = 0;
  NavItem.extraNavItemDescriptors.length = 0;
}

export function addNavItem(item) {
  NavItem.extraNavItemDescriptors.push(item);
}
